import React from 'react'

import { Box, Button, Form, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TextInput from 'modules/core/components/Inputs/Text'
import Toggler from 'modules/core/components/Toggler'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import EmployeeCodeConfigurationForm from 'modules/payroll_integrations_a3nom/lib/forms/EmployeeCodeConfigurationForm'
import a3nomConfigs from 'modules/payroll_integrations_a3nom/store/collections/A3nomConfigs'
import type A3nomConfig from 'modules/payroll_integrations_a3nom/store/models/A3nomConfig'

// The default value for the next value field after activating auto-numbering will be ‘1’.
const DEFAULT_VALUE = '1'
const DEFAULT_PREFIX = 'FA'

const EmployeeCodeConfiguration = () => {
  const [autonumberingEnabled, setAutonumberingEnabled] = React.useState<boolean>(false)
  const [a3nomConfig, setA3nomConfig] = React.useState<A3nomConfig>()
  const { loading } = useAsyncData(() => a3nomConfigs.fetch().then(initializeValues), [])

  const {
    company: { id: companyId },
  } = useCurrentSession()
  const allBorder = { all: 'abs008' } as const
  const onlyTopBorder = { topRight: 'abs008', topLeft: 'abs008' } as const
  const { navigation } = useNavigation()
  const form = useLazyRef(
    () => new EmployeeCodeConfigurationForm({ company_id: companyId })
  ).current

  const initializeValues = () => {
    const config = a3nomConfigs.find((config) => config.attributes.company_id === companyId)

    setA3nomConfig(config)

    if (config !== undefined) {
      const number = config.attributes.autonumbering_starts_on
      const code_prefix = config.attributes.code_prefix

      form.setValues({
        code_prefix: code_prefix,
        next_value: number,
      })

      setAutonumberingEnabled(number !== undefined)
    }
  }

  const createA3nomConfig = (code_prefix: string, value: string) => {
    a3nomConfigs
      .create({
        autonumbering_starts_on: value,
        code_prefix: code_prefix,
        company_id: companyId,
      })
      .then(() => a3nomConfigs.fetch().then(initializeValues))
  }

  const setToggle = async (toggleIsActive: boolean) => {
    setAutonumberingEnabled(toggleIsActive)

    if (toggleIsActive) createA3nomConfig(DEFAULT_PREFIX, DEFAULT_VALUE)

    if (!toggleIsActive && a3nomConfig !== undefined) {
      await a3nomConfig.delete({
        id: a3nomConfig.attributes.id,
      })
      form.setValues({
        next_value: '',
      })
    }

    setA3nomConfig(undefined)
  }

  const nextTwoNumbers = () => {
    const number = form.get('next_value').value
    const prefix = form.get('code_prefix').value
    if (number != '') {
      const numberOne = parseInt(number)
      const numberTwo = parseInt(number) + 1
      return `${prefix}${numberOne}, ${prefix}${numberTwo}`
    }

    return number
  }

  const finalCodeErrorMessage = () => {
    return form.isFinalCodeValid
      ? ''
      : i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.code_too_long'
        )
  }

  const validateNextValue = () => {
    return form.isNextValueValid
      ? finalCodeErrorMessage()
      : i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.invalid_next_value'
        )
  }

  const validateCodePrefix = () => {
    return form.isPrefixValid
      ? finalCodeErrorMessage()
      : i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.invalid_prefix'
        )
  }

  const { running, performAction: onSubmit } = useAsyncAction(async () => {
    if (form.get('next_value').value === '') return

    if (a3nomConfig != undefined) {
      await a3nomConfig.update({
        id: a3nomConfig.attributes.id,
        code_prefix: form.get('code_prefix').value,
        autonumbering_starts_on: form.get('next_value').value,
      })
    } else {
      await createA3nomConfig(form.get('code_prefix').value, form.get('next_value').value)
    }
  })

  if (loading) return null

  return (
    <Section
      description={i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.description'
      )}
      illustration="window-user"
      title={i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.title'
      )}
    >
      <Box
        padding="s24"
        borderRadius={autonumberingEnabled ? onlyTopBorder : allBorder}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
      >
        <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
          <Text.Normal weight="medium">
            {i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.toggle.title'
            )}
          </Text.Normal>
          <Toggler
            active={autonumberingEnabled}
            onChange={() => setToggle(!autonumberingEnabled)}
          />
        </Box>

        <Text.Normal size="200">
          {i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.toggle.description'
          )}
        </Text.Normal>
      </Box>

      {autonumberingEnabled && (
        <Box
          background="grey200"
          padding="s32"
          gap="s24"
          borderRadius={{ bottomRight: 'abs008', bottomLeft: 'abs008' }}
        >
          <Form padding="s0">
            <Box flexDirection="row" gap="s8">
              <TextInput
                field={form.get('code_prefix')}
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.code_prefix'
                )}
                validation={validateCodePrefix}
              />

              <TextInput
                field={form.get('next_value')}
                label={i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.label'
                )}
                validation={validateNextValue}
              />
            </Box>

            <Box paddingTop="s8">
              <Text.Normal size="200">
                {i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.hint',
                  { next_two_numbers: nextTwoNumbers() }
                )}
              </Text.Normal>
            </Box>
          </Form>

          <Form.Buttons>
            <Box alignItems="flexEnd">
              <Button onClick={onSubmit} disabled={running || !form.isInputValid}>
                {i18n.t(
                  'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.next_value.submit'
                )}
              </Button>
            </Box>
          </Form.Buttons>
        </Box>
      )}

      <Box
        background="grey200"
        padding="s24"
        borderRadius={allBorder}
        gap="s16"
        style={{ marginTop: '40px' }}
      >
        <Text.Normal>
          {i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.autonumbering_employee_code_configuration.help.description'
          )}
        </Text.Normal>

        <Button.Router to={navigation.employees.fullPath()} type="secondary">
          {i18n.t('codes.employees_data.button')}
        </Button.Router>
      </Box>
    </Section>
  )
}

export default observer(EmployeeCodeConfiguration)
